import { ChangeEvent, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useDebounce } from '@uidotdev/usehooks';
import { QrTable } from './QrTable/QrTable';
import { Board } from '~/components/Shared/Layout/Board/Board';
import { ErrorMessage } from '~/components/Shared/ErrorMessage/ErrorMessage';
import { Button, InputBar, Pagination, Spinner } from '~/ui';
import { TableSelect } from '~/ui/TableSelect/TableSelect';
import { TableAmountPage } from '~/ui/TableAmountPage/TableAmountPage';
import { useLocalStorage } from '~/utils/useLocalStorage';
import { useTranslation } from 'react-i18next';
import { useToastError } from '~/utils/useToastError';

import styles from './QrBoard.module.scss';
import '~/assets/css/select-table.css';
import { getPaginate } from '~/utils/getPaginate';
import { useSearchParams } from 'react-router-dom';

interface SelectedPage {
  selected: number;
}

interface Points {
  page: number;
}

export const QrBoard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { initialValues, storedInitialValues, create } =
    useLocalStorage<Points>({
      initialValues: { page: 15 },
      key: 'qr-points',
      exclude: [],
    });
  const [sort, setSort] = useState({
    field: '',
    direction: '',
  });
  const [perPage, setPerPage] = useState(
    storedInitialValues?.page || initialValues.page
  );
  const { value, paginate } = getPaginate();
  const [page, setPage] = useState<number>(paginate);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const toastError = useToastError();
  const client = useQueryClient();
  const { t } = useTranslation();

  useEffect(() => {
    create({ page: perPage });
    searchParams.delete(value)
    setSearchParams(searchParams)
    setPage(1)
  }, [perPage]);

  const params = {
    sort: sort.direction,
    perPage,
    field: sort.field,
    query: debouncedSearchTerm,
    page,
  };

  const { data, isError, error } = useQuery({
    queryFn: async () => {
      const response = await instance.get('admin/qr-generate-object', {
        params,
      });
      return response.data;
    },
    queryKey: ['qr', params],
    keepPreviousData: true,
  });

  const qrList = data?.data;

  let content: React.ReactNode;

  const handleSelect = (id: number) => {
    if (selectedItems.includes(id)) {
      const filteredUsers = selectedItems.filter(
        selectedId => selectedId !== id
      );
      setSelectedItems(filteredUsers);
    } else {
      setSelectedItems([...selectedItems, id]);
    }
  };

  const handleToggleAll = () => {
    if (selectedItems.length === qrList.length) {
      setSelectedItems([]);
    } else {
      const userIdArray = qrList.map((user: any) => user.id);
      setSelectedItems(userIdArray);
    }
  };

  const handleDeleteSelected = async () => {
    try {
      await instance.delete('admin/qr-generate-object-many', {
        data: { qr_code_ids: selectedItems },
      });
      setSelectedItems([]);
      client.invalidateQueries(['qr']);
    } catch (error) {
      toastError(error);
    }
  };

  if (qrList) {
    content = (
      <QrTable
        qrList={qrList}
        sort={sort}
        setSort={setSort}
        selectedItems={selectedItems}
        handleToggleAll={handleToggleAll}
        handleToggle={handleSelect}
      />
    );
  } else if (isError) {
    content = <ErrorMessage error={error} />;
  } else {
    content = <Spinner />;
  }

  return (
    <Board>
      <div className={styles.qrTableOptions}>
        <TableSelect perPage={perPage} setPerPage={setPerPage} />
        <div className={styles.tableHeadRightWrapper}>
          {!!selectedItems.length && (
            <Button
              text={t('delete')}
              color="red"
              onClick={handleDeleteSelected}
              className={styles.deleteButton}
            />
          )}
          <InputBar
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            value={searchTerm}
            placeholder={t('category_search')}
            classNameInput={styles.qrTableInput}
            label={`${t('search')}:`}
            star={false}
            classNameInputWrapper={styles.qrTableInputWrapper}
          />
        </div>
      </div>
      {content}
      <div className={styles.qrTablePagination}>
        <TableAmountPage
          firstRow={data?.from}
          lastRow={data?.to}
          total={data?.total}
        />
        <Pagination
          forcePage={paginate}
          pageCount={data?.last_page}
          onPageChange={(selectedPage: SelectedPage) => {
            setPage(selectedPage.selected + 1);
          }}
        />
      </div>
    </Board>
  );
};
